/**
 * One source of truth for page metadata.
 *
 * Read twice: by `useDocumentMeta` at runtime (JS-rendering crawlers like
 * Googlebot), and by the prerender step in vite.config.ts, which writes a
 * `<route>.html` per page and language with these tags baked in for
 * crawlers that never run JS (LinkedIn, Slack, WhatsApp, X previews). Keep
 * this module free of `@/` imports and browser APIs so the build can load it.
 */

export const SITE_URL = 'https://softmaze.at'
export const OG_IMAGE = `${SITE_URL}/og.png`

/**
 * Site languages. German is the default and lives at the bare paths
 * (`/`, `/services`, …); English mirrors them under `/en` (`/en`, `/en/services`,
 * …). Each language is its own prerendered URL, so both get indexed, and
 * the two are linked with hreflang.
 */
export type Locale = 'de' | 'en'
export const LOCALES: Locale[] = ['de', 'en']
export const DEFAULT_LOCALE: Locale = 'de'

/** `/services` in `en` → `/en/services`; `/` in `en` → `/en`; `de` is unprefixed. */
export function localePath(path: string, locale: Locale): string {
  if (locale === DEFAULT_LOCALE) return path
  return path === '/' ? `/${locale}` : `/${locale}${path}`
}

/** Inverse of `localePath`: `/en/services` → { locale: 'en', path: '/services' }. */
export function splitLocale(pathname: string): { locale: Locale; path: string } {
  for (const locale of LOCALES) {
    if (locale === DEFAULT_LOCALE) continue
    if (pathname === `/${locale}`) return { locale, path: '/' }
    if (pathname.startsWith(`/${locale}/`)) return { locale, path: pathname.slice(locale.length + 1) }
  }
  return { locale: DEFAULT_LOCALE, path: pathname }
}

/** The locale doubles as the `<html lang>`, hreflang and `inLanguage` value. */
export const OG_LOCALE: Record<Locale, string> = { de: 'de_AT', en: 'en_US' }

export type PageMeta = {
  title: string
  description: string
  /** e.g. 'noindex' */
  robots?: string
}

/**
 * Paths the site serves as their own page (and prerenders), per language.
 * Titles stay within ~60 characters and descriptions within 120–160, unique
 * across both languages (seo.test.ts).
 */
export const PAGES: Record<string, Record<Locale, PageMeta>> = {
  '/': {
    de: {
      title: 'Softmaze — Softwareentwicklung in Wien | Web & Apps',
      description:
        'SoftMaze Solutions GmbH ist ein Software-Studio in Wien. Wir planen und bauen Webplattformen, Dashboards und Schnittstellen für Unternehmen in Österreich.',
    },
    en: {
      title: 'Softmaze — Software Studio in Vienna | Web & App Development',
      description:
        'SoftMaze Solutions GmbH is a Vienna software studio. We design and build web platforms, dashboards and integrations for businesses across Austria.',
    },
  },
  '/services': {
    de: {
      title: 'Leistungen — Softmaze: Strategie, Design & Entwicklung',
      description:
        'Was Softmaze macht: Produktstrategie, UX/UI-Design, Full-Stack-Entwicklung, DevOps und Automatisierung — ein Team vom ersten Gespräch bis zum laufenden System.',
    },
    en: {
      title: 'Services — Softmaze: Strategy, Design & Engineering, Vienna',
      description:
        'What Softmaze does: product strategy, UX/UI design, full-stack engineering, DevOps and automation — one team from first conversation to running system.',
    },
  },
  '/projects': {
    de: {
      title: 'Projekte & Live-Sandboxes — Softmaze, Wien',
      description:
        'Plattformen von Softmaze für Logistik, Zustellung und Kfz-Werkstätten. Öffnen Sie eine Sandbox und testen Sie die echten Apps im Browser, ohne Anmeldung.',
    },
    en: {
      title: 'Projects & Live Sandboxes — Softmaze, Vienna',
      description:
        'Platforms Softmaze has built for logistics, last-mile delivery and automotive workshops. Open a sandbox and try the real apps in your browser, no sign-up.',
    },
  },
  '/contact': {
    de: {
      title: 'Kontakt — Softmaze, Software-Studio in Wien',
      description:
        'Erzählen Sie uns, was Sie bauen oder wo es hakt. Per E-Mail, Telefon oder WhatsApp, auf Deutsch oder Englisch — Antwort innerhalb eines Werktags.',
    },
    en: {
      title: 'Contact Softmaze — Software Studio in Vienna, Austria',
      description:
        'Tell Softmaze what you are building or what is stuck. Email, phone or WhatsApp, English or German — we reply within one working day. Vienna, Austria.',
    },
  },
  '/faq': {
    de: {
      title: 'FAQ — So arbeitet Softmaze | Web- & App-Entwicklung, Wien',
      description:
        'Kurze Antworten zur Zusammenarbeit mit Softmaze: für wen wir arbeiten, Design und Entwicklung, Technologien, Cloud-Hosting, Zeitplan und Support.',
    },
    en: {
      title: 'FAQ — How Softmaze Works | Web & App Development, Vienna',
      description:
        'Short answers on working with Softmaze: who we work with, design and development, technologies, cloud hosting, timelines and support after launch.',
    },
  },
  '/imprint': {
    de: {
      title: 'Impressum — SoftMaze Solutions GmbH',
      description:
        'Impressum der SoftMaze Solutions GmbH, Gablenzgasse 21, 1150 Wien, Österreich: Angaben zum Unternehmen, Kontakt, Haftung für Inhalte und Urheberrecht.',
      robots: 'noindex, follow',
    },
    en: {
      title: 'Imprint — SoftMaze Solutions GmbH',
      description:
        'Legal disclosure (Impressum) of SoftMaze Solutions GmbH, Gablenzgasse 21, 1150 Vienna, Austria: company details, contact, liability and copyright.',
      robots: 'noindex, follow',
    },
  },
  '/privacy': {
    de: {
      title: 'Datenschutzerklärung — SoftMaze Solutions GmbH',
      description:
        'Welche Daten softmaze.at verarbeitet: Hosting, Kontakt per E-Mail oder Messenger, Google Maps nur nach Klick, kein Tracking. Ihre Rechte nach der DSGVO.',
      robots: 'noindex, follow',
    },
    en: {
      title: 'Privacy Policy — SoftMaze Solutions GmbH',
      description:
        'What data softmaze.at processes: hosting, contact by email or messenger, Google Maps only after a click, no tracking. Your rights under the GDPR.',
      robots: 'noindex, follow',
    },
  },
  '/nisg-2026': {
    de: {
      title: 'NISG 2026: Cybersecurity-Check & Umsetzung | Softmaze Wien',
      description:
        'Das NISG 2026 gilt seit 1. Oktober 2026. Softmaze prüft, ob Ihr Unternehmen betroffen ist, schließt die Lücken und registriert Sie fristgerecht. Aus Wien.',
    },
    en: {
      title: 'NISG 2026: Cybersecurity Check & Implementation | Softmaze',
      description:
        'The NISG 2026 has applied since 1 October 2026. Softmaze checks whether your company is covered, closes the gaps and gets you registered in time, from Vienna.',
    },
  },
  '/archive': {
    de: {
      title: 'Archiv — Nutzertests & Umfragen | Softmaze',
      description:
        'Das Archiv von Softmaze: laufende Nutzertests und Umfragen zum Mitmachen, worum es jeweils geht, wie lange es dauert und was mit den Antworten passiert.',
      robots: 'noindex, follow',
    },
    en: {
      title: 'Archive — User Tests & Surveys | Softmaze',
      description:
        'The Softmaze archive: ongoing user tests and surveys you can take part in, what each one is about, how long it takes and what happens to your answers.',
      robots: 'noindex, follow',
    },
  },
}

/** Sandbox start pages, keyed by project id (see sandboxProjects.ts). */
export const SANDBOX_NAMES: Record<string, string> = {
  vollgas: 'Vollgas KFZ',
  pjlogistik: 'PJ Logistik',
  gladiator: 'Gladiator Logistik',
}

export function sandboxMeta(name: string, locale: Locale): PageMeta {
  return locale === 'de'
    ? {
        title: `${name} Sandbox — Live-Demo testen | Softmaze`,
        description: `Testen Sie ${name} von Softmaze direkt im Browser: die echten Apps mit Beispieldaten, in Ihrer eigenen privaten Kopie. Ohne Konto, ohne Anmeldung.`,
      }
    : {
        title: `${name} Sandbox — Try the Live Demo | Softmaze`,
        description: `Try ${name}, built by Softmaze, right in your browser: the real apps running on sample data in your own private copy. No account or sign-up needed.`,
      }
}

/**
 * Served as dist/404.html (Firebase returns it with a 404 status) for any
 * unknown path. The file is the German prerender; under /en/… the client
 * renders it fresh in English (main.tsx).
 */
export const NOT_FOUND: Record<Locale, PageMeta> = {
  de: {
    title: 'Seite nicht gefunden — Softmaze',
    description:
      'Diese Seite gibt es nicht oder nicht mehr. SoftMaze Solutions GmbH ist ein Software-Studio in Wien — sehen Sie sich unsere Projekte und Leistungen an.',
    robots: 'noindex, follow',
  },
  en: {
    title: 'Page not found — Softmaze',
    description:
      'This page does not exist or has moved. SoftMaze Solutions GmbH is a software studio in Vienna — see our projects, services and contact details instead.',
    robots: 'noindex, follow',
  },
}

/** Metadata for an unprefixed path (`/services`, `/demo/vollgas`) in one language. */
export function metaForPath(path: string, locale: Locale): PageMeta {
  const page = PAGES[path]
  if (page) return page[locale]
  const m = path.match(/^\/demo\/([a-z0-9-]+)$/)
  if (m && SANDBOX_NAMES[m[1]]) return sandboxMeta(SANDBOX_NAMES[m[1]], locale)
  return PAGES['/'][locale]
}

/** Absolute URL of an unprefixed path in one language (its canonical). */
export function urlFor(path: string, locale: Locale): string {
  return `${SITE_URL}${localePath(path, locale)}`
}

/**
 * hreflang alternates for an unprefixed path: one per language plus
 * `x-default` (German, the site's default). Every language version lists
 * the same set, itself included, as Google requires.
 */
export function alternatesFor(path: string): { hreflang: string; href: string }[] {
  return [
    ...LOCALES.map((locale) => ({ hreflang: locale, href: urlFor(path, locale) })),
    { hreflang: 'x-default', href: urlFor(path, DEFAULT_LOCALE) },
  ]
}

const CRUMB_NAMES: Record<string, Record<Locale, string>> = {
  '/': { de: 'Start', en: 'Home' },
  '/services': { de: 'Leistungen', en: 'Services' },
  '/projects': { de: 'Projekte', en: 'Projects' },
  '/contact': { de: 'Kontakt', en: 'Contact' },
  '/faq': { de: 'FAQ', en: 'FAQ' },
  '/imprint': { de: 'Impressum', en: 'Imprint' },
  '/privacy': { de: 'Datenschutz', en: 'Privacy' },
  '/archive': { de: 'Archiv', en: 'Archive' },
  '/nisg-2026': { de: 'NISG 2026', en: 'NISG 2026' },
}

/** schema.org BreadcrumbList for a subpage; null for the home page. */
export function breadcrumbSchema(path: string, locale: Locale): Record<string, unknown> | null {
  const items: { name: string; url: string }[] = [{ name: CRUMB_NAMES['/'][locale], url: urlFor('/', locale) }]
  const demo = path.match(/^\/demo\/([a-z0-9-]+)$/)
  if (demo && SANDBOX_NAMES[demo[1]]) {
    items.push({ name: CRUMB_NAMES['/projects'][locale], url: urlFor('/projects', locale) })
    items.push({ name: `${SANDBOX_NAMES[demo[1]]} Sandbox`, url: urlFor(path, locale) })
  } else if (path !== '/' && CRUMB_NAMES[path]) {
    items.push({ name: CRUMB_NAMES[path][locale], url: urlFor(path, locale) })
  } else {
    return null
  }
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  }
}
